import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import customFetchBase from '../expiredToken/expiredToken';
import { IAddEvent, IEvent } from '../../types/events/eventsInterface';
import { selectEventUrl } from '../../utils/event';

const customFetch = customFetchBase(config.baseUrlDealers);

export const apiEvent = createApi({
  reducerPath: 'apiLocations',
  baseQuery: customFetch,
  endpoints: (builder) => ({
    getEventListByLocation: builder.mutation<[], number>({
      query: (locationId) => ({
        url: `/api/Event/EventLightZones?locationId=${locationId}`,
        method: 'GET'
      })
    }),
    getEvenDetails: builder.mutation<IEvent, number>({
      query: (eventId) => ({
        url: `/api/Event/LightZoneEventDetails?eventId=${eventId}`,
        method: 'GET'
      })
    }),
    postEventScene: builder.mutation<[], {}>({
      query: (event) => ({
        url: '/api/Event/Scene',
        method: 'POST',
        body: event
      })
    }),
    postEventOn: builder.mutation<[], {}>({
      query: (event) => ({
        url: '/api/Event/On',
        method: 'POST',
        body: event
      })
    }),
    postEventOff: builder.mutation<[], {}>({
      query: (event) => ({
        url: '/api/Event/Off',
        method: 'POST',
        body: { ...event }
      })
    }),
    postEventColor: builder.mutation<[], {}>({
      query: (event) => ({
        url: '/api/Event/Color',
        method: 'POST',
        body: event
      })
    }),
    postEventEffect: builder.mutation<[], {}>({
      query: (event) => ({
        url: '/api/Event/Effect',
        method: 'POST',
        body: event
      })
    }),
    postEventSequence: builder.mutation<[], {}>({
      query: (event) => ({
        url: '/api/Event/Sequence',
        method: 'POST',
        body: event
      })
    }),
    deleteEvent: builder.mutation<[], number>({
      query: (eventId) => ({
        url: `/api/Event/Delete/${eventId}`,
        method: 'DELETE'
      })
    }),
    deleteMultipleEvents: builder.mutation<[], number[]>({
      query: (ids) => ({
        url: `/api/Event?Ids=${ids.map((id) => id).join(',')}`,
        method: 'DELETE'
      })
    }),
    eventsToExecuteByDateAndLocation: builder.mutation<
      number[],
      { date: string; locationId: number }
    >({
      query: (event) => ({
        url: '/api/Event/EventsToExecuteByDate',
        method: 'GET',
        params: {
          executionDate: event.date,
          locationId: event.locationId
        }
      })
    }),
    postEventExecute: builder.mutation<[], number>({
      query: (eventId) => ({
        url: `/api/Commands/Event/Execute`,
        method: 'POST',
        body: { eventId, duration: 0, brightness: 0 }
      })
    }),
    changeEventStatus: builder.mutation<
      [],
      { eventId: number; status: boolean }
    >({
      query: (status) => ({
        url: `/api/Event/SetStatus/${status.eventId}/${status.status}`,
        method: 'PUT'
      })
    }),
    changeEventsStatus: builder.mutation<
      [],
      { eventIds: number[]; areEnabled: boolean }
    >({
      query: (body) => ({
        url: `/api/Event/UpdateStatuses`,
        method: 'PUT',
        body
      })
    }),
    editEvent: builder.mutation<[], IAddEvent>({
      query: (event) => ({
        url: `/api/${selectEventUrl(event.eventType)}/${event.eventId}`,
        method: 'PUT',
        body: event
      })
    }),
    putEventOff: builder.mutation<[], IAddEvent>({
      query: (event) => ({
        url: `/api/Event/Off/${event.eventId}`,
        method: 'PUT',
        body: { ...event }
      })
    }),
    putEventColor: builder.mutation<[], IAddEvent>({
      query: (event) => ({
        url: `/api/Event/Color/${event.eventId}`,
        method: 'PUT',
        body: event
      })
    }),
    putEventEffect: builder.mutation<[], IAddEvent>({
      query: (event) => ({
        url: `/api/Event/Effect/${event.eventId}`,
        method: 'PUT',
        body: event
      })
    }),
    putEventScene: builder.mutation<[], IAddEvent>({
      query: (event) => ({
        url: `/api/Event/Scene/${event.eventId}`,
        method: 'PUT',
        body: event
      })
    })
  })
});

export const {
  useGetEventListByLocationMutation,
  usePostEventSceneMutation,
  usePostEventOnMutation,
  usePostEventOffMutation,
  usePostEventColorMutation,
  usePostEventEffectMutation,
  usePostEventSequenceMutation,
  useDeleteEventMutation,
  useEventsToExecuteByDateAndLocationMutation,
  usePostEventExecuteMutation,
  useChangeEventStatusMutation,
  useGetEvenDetailsMutation,
  useEditEventMutation,
  useDeleteMultipleEventsMutation,
  useChangeEventsStatusMutation,
  usePutEventColorMutation,
  usePutEventEffectMutation,
  usePutEventOffMutation,
  usePutEventSceneMutation
} = apiEvent;
